import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Trophy, Plus, Coins, Users, MessageSquare, Clock, Crown, Activity, Sparkles } from "lucide-react";
import PayoutCard from "@/components/PayoutCard";
import EarningsPanel from "@/components/EarningsPanel";
import ObsSetup from "@/components/ObsSetup";
import ChannelChatSettings from "@/components/ChannelChatSettings";
import ChannelSubEconomySettings from "@/components/ChannelSubEconomySettings";
import StripeConnectPanel from "@/components/StripeConnectPanel";
import ModerationCard from "@/components/ModerationCard";
import AutoModCard from "@/components/AutoModCard";
import StaffCard from "@/components/StaffCard";
import ChallengesCard from "@/components/ChallengesCard";
import KYCCard from "@/components/KYCCard";

const CATEGORIES = [
  "Just Chatting", "IRL", "Music", "Art", "Cooking", "Tech", "Esports",
  "Tic-Tac-Toe", "Poker", "Chess",
  "Crypto Gambling", "Slots", "Casino Games", "Sports Betting",
];

const SOCIAL_KEYS = ["steam", "twitch", "kick", "youtube", "tiktok", "reddit", "facebook", "twitter", "instagram", "discord"];
const SOCIAL_LABELS = {
  steam: "Steam profile URL",
  twitch: "Twitch URL",
  kick: "Kick URL",
  youtube: "YouTube URL",
  tiktok: "TikTok URL",
  reddit: "Reddit URL",
  facebook: "Facebook URL",
  twitter: "X / Twitter URL",
  instagram: "Instagram URL",
  discord: "Discord invite URL",
};

export default function Dashboard() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [channel, setChannel] = useState(null);
  const [form, setForm] = useState({ title: "", category: "Just Chatting", description: "", contest_rules: "", thumbnail_url: "" });
  const [socials, setSocials] = useState({});
  const [earnings, setEarnings] = useState({ total: 0, entries: [] });
  const [tips, setTips] = useState({ total_credits: 0, tips: [] });
  const [saved, setSaved] = useState(false);

  const [catalog, setCatalog] = useState([]);
  const [tForm, setTForm] = useState({ name: "", game_slug: "ttt", prize: "", starts_at: "" });
  const [tournaments, setTournaments] = useState([]);
  const [tBusy, setTBusy] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    api.get(`/channels/${user.username}`).then(r => {
      setChannel(r.data);
      setForm({
        title: r.data.title,
        category: r.data.category || "Just Chatting",
        description: r.data.description || "",
        contest_rules: r.data.contest_rules || "",
        thumbnail_url: r.data.thumbnail_url || "",
      });
      setSocials(r.data.social_links || {});
    });
    api.get("/streamer/earnings").then(r => setEarnings(r.data));
    api.get("/tips/received").then(r => setTips(r.data));
    api.get("/games/catalog").then(r => setCatalog(r.data));
    api.get("/tournaments").then(r => setTournaments(r.data));
    api.get("/dashboard/analytics").then(r => setAnalytics(r.data)).catch(() => {});
    refreshMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username]);

  const save = async () => {
    await api.patch("/channels/me", { ...form, social_links: socials });
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  };

  const createTournament = async (e) => {
    e.preventDefault();
    if (!tForm.name || !tForm.prize || !tForm.starts_at) return;
    setTBusy(true);
    try {
      const iso = new Date(tForm.starts_at).toISOString();
      await api.post("/tournaments", { ...tForm, starts_at: iso });
      const r = await api.get("/tournaments");
      setTournaments(r.data);
      setTForm({ name: "", game_slug: "ttt", prize: "", starts_at: "" });
    } finally { setTBusy(false); }
  };

  if (!user) return null;

  const myTournaments = tournaments.filter(t => t.host_username === user.username);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
      <div className="sc-label">Streamer Dashboard</div>
      <h1 className="sc-heading text-4xl font-black mt-1 mb-8">Welcome back, {user.username}</h1>

      <div className="grid lg:grid-cols-4 gap-4 mb-8">
        <div className="sc-card p-5">
          <div className="sc-label text-[#00E5FF]">Tier</div>
          <div className="sc-heading text-2xl font-bold mt-2 capitalize">{user.tier}</div>
          <Link to="/account/plans" className="sc-btn-ghost text-xs mt-3 inline-block">Manage</Link>
        </div>
        <div className="sc-card p-5">
          <div className="sc-label text-[#10B981]">Sub Earnings</div>
          <div className="sc-heading text-2xl font-bold mt-2">${earnings.total.toFixed(2)}</div>
          <div className="text-xs text-zinc-400 mt-2">70% share</div>
        </div>
        <div className="sc-card p-5">
          <div className="sc-label text-[#FFD700]">Gold Coins</div>
          <div className="sc-heading text-2xl font-bold mt-2 flex items-center gap-1.5">
            <Coins className="w-5 h-5 text-[#FFD700]" /> {user.gold_coins ?? 0}
          </div>
          <div className="text-xs text-zinc-400 mt-2">+{tips.total_credits} tips (100%)</div>
        </div>
        <div className="sc-card p-5">
          <div className="sc-label" style={{ color: "#00E5FF" }}>Sparks</div>
          <div className="sc-heading text-2xl font-bold mt-2 flex items-center gap-1.5">
            <Sparkles className="w-5 h-5" style={{ color: "#00E5FF" }} /> {user.sparks ?? user.credits ?? 0}
          </div>
          <div className="text-xs text-zinc-400 mt-2">Free play · games + shop</div>
        </div>
        <div className="sc-card p-5">
          <div className="sc-label text-[#FF3366]">Status</div>
          <div className="sc-heading text-2xl font-bold mt-2">{channel?.is_live ? "LIVE" : (user.is_verified_streamer ? "Offline" : "Unverified")}</div>
          {user.is_verified_streamer ? (
            <Link data-testid="dashboard-open-studio" to={`/c/${user.username}`} className="sc-btn text-xs mt-3 inline-block">Open Studio</Link>
          ) : (
            <Link data-testid="dashboard-verify-cta" to="/account?tab=verify" className="sc-btn text-xs mt-3 inline-block" style={{ background: "#F59E0B", color: "#0A0A0C" }}>Verify to Stream</Link>
          )}
        </div>
      </div>

      {!user.is_verified_streamer && (
        <div className="mb-6"><KYCCard /></div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="sc-card p-6 space-y-4">
          <div className="sc-heading text-xl font-bold">Channel Settings</div>
          <div>
            <label className="sc-label block mb-2">Stream Title</label>
            <input data-testid="dashboard-title-input" value={form.title} onChange={e=>setForm({...form, title: e.target.value})} className="sc-input" />
          </div>
          <div>
            <label className="sc-label block mb-2">Category</label>
            <select data-testid="dashboard-category" value={form.category} onChange={e=>setForm({...form, category: e.target.value})} className="sc-input">
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="text-[10px] text-zinc-500 mt-1">
              Streaming external gambling sites (Stake, Rainbet, Clash.gg etc)? Pick a Gambling category — viewers see a clear 3rd-party disclaimer.
            </div>
          </div>
          <div>
            <label className="sc-label block mb-2">Stream Thumbnail <span className="text-zinc-500 normal-case tracking-normal">(optional — a wide 16:9 promo image looks best)</span></label>
            <input
              data-testid="dashboard-thumbnail-input"
              value={form.thumbnail_url}
              onChange={e => setForm({ ...form, thumbnail_url: e.target.value })}
              placeholder="https://images.example.com/my-stream-cover.jpg"
              className="sc-input"
            />
            {form.thumbnail_url ? (
              <div className="mt-2">
                <div className="aspect-video max-w-xs bg-black rounded-sm border border-white/10 overflow-hidden">
                  <img
                    data-testid="dashboard-thumbnail-preview"
                    src={form.thumbnail_url}
                    alt="Stream thumbnail preview"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.opacity = "0.25"; }}
                  />
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">Preview · this replaces the generic cover on the Browse page.</div>
              </div>
            ) : (
              <div className="text-[10px] text-zinc-500 mt-1">
                Leave blank and we&apos;ll use your profile photo (or a themed default if you haven&apos;t set one).
                {" "}<a href="/account" className="text-[color:var(--sc-cyan)] underline">Change PFP →</a>
              </div>
            )}
          </div>
          <div>
            <label className="sc-label block mb-2">Bio / Description</label>
            <textarea data-testid="dashboard-description" value={form.description} onChange={e=>setForm({...form, description: e.target.value})} className="sc-input min-h-[80px]" />
          </div>
          <div>
            <label className="sc-label block mb-2">Active Contest Rules <span className="text-zinc-500 normal-case tracking-normal">(optional — your own contest, your own prizes)</span></label>
            <textarea data-testid="dashboard-contest" value={form.contest_rules} onChange={e=>setForm({...form, contest_rules: e.target.value})} placeholder="Top 3 in tonight's Connect 4 win a custom emote pack!" className="sc-input min-h-[80px]" />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={save} className="sc-btn" data-testid="dashboard-save-channel">Save Channel</button>
            {saved && <span className="text-xs text-[#10B981]">Saved ✓</span>}
          </div>
        </div>

        <div className="sc-card p-6 space-y-4">
          <div className="sc-heading text-xl font-bold">Connected Profiles</div>
          <p className="text-xs text-zinc-400">Paste your public profile URLs. They show as badges on your channel page.</p>
          <div className="grid grid-cols-2 gap-3">
            {SOCIAL_KEYS.map(k => (
              <div key={k}>
                <label className="text-[10px] tracking-widest uppercase text-zinc-400 block mb-1">{k}</label>
                <input
                  data-testid={`social-input-${k}`}
                  value={socials[k] || ""}
                  onChange={e => setSocials({ ...socials, [k]: e.target.value })}
                  placeholder={SOCIAL_LABELS[k]}
                  className="sc-input text-xs"
                />
              </div>
            ))}
          </div>
          <button onClick={save} className="sc-btn-ghost text-xs" data-testid="dashboard-save-socials">Save Profile Links</button>
        </div>
      </div>

      <div className="mt-4">
        <EarningsPanel />
      </div>

      <div className="mt-6">
        <PayoutCard earnings={earnings} />
      </div>

      <div className="mt-4">
        <ObsSetup />
      </div>

      {analytics && (
        <div className="sc-card p-6 mt-4" data-testid="community-analytics">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-[color:var(--sc-volt)]" />
            <div className="sc-heading text-xl font-bold">Community Analytics</div>
            <Link to={`/c/${user.username}`} className="ml-auto sc-btn-ghost text-xs" data-testid="community-analytics-open">View Public Hub →</Link>
          </div>
          <div className="grid sm:grid-cols-4 gap-3 mb-6">
            <MetricBox icon={Crown} label="Community Lvl" value={analytics.community_level} tone="var(--sc-volt)" />
            <MetricBox icon={Users} label="Members" value={analytics.member_count} tone="var(--sc-cyan)" />
            <MetricBox icon={Activity} label="Active 7d" value={analytics.active_7d} tone="var(--sc-pink)" />
            <MetricBox icon={Coins} label="Tips (credits)" value={analytics.revenue.tips_credits} tone="var(--sc-orange)" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            <MetricBox icon={MessageSquare} label="Total Chats" value={analytics.totals.chats.toLocaleString()} tone="var(--sc-pink)" />
            <MetricBox icon={Clock} label="Watch Minutes" value={analytics.totals.watch_minutes.toLocaleString()} tone="var(--sc-cyan)" />
            <MetricBox icon={Coins} label="Sub Revenue" value={`$${analytics.revenue.subs_usd.toFixed(2)}`} tone="var(--sc-volt)" />
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <div>
              <div className="sc-label mb-2">Top Supporters</div>
              {analytics.top_supporters.length === 0 && <div className="text-xs text-zinc-500">No tips yet.</div>}
              <div className="space-y-1">
                {analytics.top_supporters.slice(0, 5).map((s, i) => (
                  <div key={s.username} className="flex items-center gap-2 text-xs p-2 border border-white/5 rounded-sm">
                    <div className="w-5 text-center sc-mono text-zinc-500">#{i+1}</div>
                    <div className="flex-1 truncate">{s.username}</div>
                    <div className="sc-mono text-[color:var(--sc-orange)] font-bold">{s.tips_total}c</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="sc-label mb-2">Most Active Viewers</div>
              {analytics.most_active.length === 0 && <div className="text-xs text-zinc-500">No activity yet.</div>}
              <div className="space-y-1">
                {analytics.most_active.slice(0, 5).map((s, i) => (
                  <div key={s.username} className="flex items-center gap-2 text-xs p-2 border border-white/5 rounded-sm">
                    <div className="w-5 text-center sc-mono text-zinc-500">#{i+1}</div>
                    <div className="flex-1 truncate">{s.username}</div>
                    <div className="sc-mono text-zinc-400">{s.chat_count}💬 · {s.watch_minutes}m</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4">
        <ChallengesCard channelUsername={user.username} />
      </div>

      <div className="mt-4">
        <ModerationCard channelUsername={user.username} />
      </div>

      <div className="mt-4">
        <StaffCard channelUsername={user.username} />
      </div>

      <div className="mt-4">
        <AutoModCard channelUsername={user.username} />
        <ChannelChatSettings ownerUsername={user.username} />
        <ChannelSubEconomySettings ownerUsername={user.username} />
        <StripeConnectPanel />
      </div>

      <div className="sc-card p-6 mt-4">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-[#F59E0B]" />
          <div className="sc-heading text-xl font-bold">Host Your Own Contest</div>
        </div>        <p className="text-xs text-zinc-400 mb-4">Create a tournament with your own non-cash prize (gift cards, custom emotes, merch, points, etc.). You fulfill the prize directly with the winner.</p>
        <form onSubmit={createTournament} className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="sc-label block mb-2">Name</label>
            <input data-testid="dashboard-tour-name" value={tForm.name} onChange={e=>setTForm({...tForm, name: e.target.value})} placeholder="Tuesday Night Showdown" className="sc-input" required />
          </div>
          <div>
            <label className="sc-label block mb-2">Game</label>
            <select data-testid="dashboard-tour-game" value={tForm.game_slug} onChange={e=>setTForm({...tForm, game_slug: e.target.value})} className="sc-input">
              {catalog.filter(g => g.available).map(g => <option key={g.slug} value={g.slug}>{g.name}</option>)}
            </select>
          </div>
          <div>
            <label className="sc-label block mb-2">Prize</label>
            <input data-testid="dashboard-tour-prize" value={tForm.prize} onChange={e=>setTForm({...tForm, prize: e.target.value})} placeholder="$25 Steam Gift Card / custom emote pack / merch" className="sc-input" required />
          </div>
          <div>
            <label className="sc-label block mb-2">Starts At</label>
            <input data-testid="dashboard-tour-starts" type="datetime-local" value={tForm.starts_at} onChange={e=>setTForm({...tForm, starts_at: e.target.value})} className="sc-input" required />
          </div>
          <div className="sm:col-span-2">
            <button data-testid="dashboard-tour-create" disabled={tBusy} className="sc-btn flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> {tBusy ? "Creating..." : "Create Tournament"}
            </button>
          </div>
        </form>

        {myTournaments.length > 0 && (
          <div className="mt-6">
            <div className="sc-label mb-3">Your Contests ({myTournaments.length})</div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {myTournaments.map(t => (
                <div key={t.id} className="flex items-center justify-between text-xs p-3 border border-white/5 rounded-sm">
                  <div>
                    <div className="font-semibold text-zinc-200">{t.name}</div>
                    <div className="text-zinc-500">{t.game_slug} · {t.participants} players · {new Date(t.starts_at).toLocaleString()}</div>
                  </div>
                  <div className="text-[#10B981] font-bold">{t.prize}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricBox({ icon: Icon, label, value, tone }) {
  return (
    <div className="p-3 border border-white/10 rounded-sm">
      <div className="flex items-center gap-1.5 sc-label" style={{ color: tone }}><Icon className="w-3 h-3" /> {label}</div>
      <div className="sc-heading text-2xl font-black mt-1">{value}</div>
    </div>
  );
}
